import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as mailService from '../services/graphMailService';

// ---------------------------------------------------------------------------
// localStorage helpers — persists custom folder order across refreshes
// ---------------------------------------------------------------------------

const LS_ORDER_KEY = 'bpa_folder_order';

function loadFolderOrder() {
  try { return JSON.parse(localStorage.getItem(LS_ORDER_KEY) || '{}'); } catch { return {}; }
}

function saveFolderOrder({ rootCustomOrder, childrenOrder } = {}) {
  try {
    const current = loadFolderOrder();
    localStorage.setItem(LS_ORDER_KEY, JSON.stringify({
      ...current,
      ...(rootCustomOrder !== undefined ? { rootCustomOrder } : {}),
      ...(childrenOrder !== undefined ? { childrenOrder } : {}),
    }));
  } catch { /* storage unavailable — order works for this session only */ }
}

/**
 * Build the final rootIds array:
 *   1. System folders locked in WELL_KNOWN_ORDER
 *   2. Custom folders in their localStorage-saved order
 *   3. Any new custom folders not yet in localStorage appended alphabetically
 *
 * Rules:
 *   - Only folders present in rawRootIds are ever included (no phantom entries).
 *   - Comparison is case-insensitive: Graph API may return 'sentItems' or
 *     'sentitems' depending on Exchange version — both match 'sentitems'.
 */
function composeSortedRootIds(byId, rawRootIds) {
  // Map each well-known slot → the actual folder ID in rawRootIds (if present).
  // Using rawRootIds.find() guarantees we only promote real root-level folders.
  const systemIds = WELL_KNOWN_ORDER
    .map(wkn =>
      rawRootIds.find(
        id => (byId[id]?.wellKnownName || '').toLowerCase() === wkn
      )
    )
    .filter(Boolean);

  const systemSet = new Set(systemIds);

  // Everything in rawRootIds that isn't a recognised system folder
  const customIds = rawRootIds.filter(id => id && byId[id] && !systemSet.has(id));

  const { rootCustomOrder = [] } = loadFolderOrder();
  const savedSet = new Set(rootCustomOrder);

  const orderedCustom = [
    // Saved order first (only IDs that still exist in this load)
    ...rootCustomOrder.filter(id => customIds.includes(id)),
    // Brand-new custom folders not yet in localStorage — append alphabetically
    ...customIds
      .filter(id => !savedSet.has(id))
      .sort((a, b) => (byId[a]?.displayName || '').localeCompare(byId[b]?.displayName || '')),
  ];

  return [...systemIds, ...orderedCustom];
}

// ---------------------------------------------------------------------------
// Async Thunks
// ---------------------------------------------------------------------------

export const fetchFolders = createAsyncThunk(
  'mailFolders/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const data = await mailService.fetchFolders();
      // data: { folders: Folder[], rootIds: string[], childrenByParent: {} }
      return data;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const fetchChildFolders = createAsyncThunk(
  'mailFolders/fetchChildren',
  async ({ parentId }, { rejectWithValue }) => {
    try {
      const data = await mailService.fetchChildFolders(parentId);
      return { parentId, folders: data.folders };
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

// ---------------------------------------------------------------------------
// Initial State
// ---------------------------------------------------------------------------

const initialState = {
  // Normalized folder entities: { [folderId]: Folder }
  byId: {},

  // Top-level folder IDs (no parent)
  rootIds: [],

  // Children per parent: { [parentFolderId]: string[] }
  childrenByParent: {},

  // Graph delta token for folder sync
  deltaToken: null,

  // UI expansion state: Set stored as array for Redux serialization
  expandedIds: [],

  // Folders currently being loaded
  loadingIds: [],

  // Overall status
  status: 'idle', // 'idle' | 'loading' | 'ready' | 'error'
  error: null,
};

// ---------------------------------------------------------------------------
// Folder shape reference:
// {
//   id: string,
//   displayName: string,
//   wellKnownName: string | null,  // 'inbox' | 'sentitems' | 'drafts' | 'deleteditems' | 'junkemail'
//   parentFolderId: string | null,
//   childFolderCount: number,
//   totalItemCount: number,
//   unreadItemCount: number,
// }
// ---------------------------------------------------------------------------

// Well-known folder display order — matches exact Outlook default
export const WELL_KNOWN_ORDER = [
  'inbox',        // 1. Inbox
  'drafts',       // 2. Drafts
  'sentitems',    // 3. Sent Items
  'deleteditems', // 4. Deleted Items
  'archive',      // 5. Archive      ← before Junk in real Outlook
  'junkemail',    // 6. Junk Email
  'outbox',       // 7. Outbox
  'rssfeeds',     // 8. RSS Feeds
];

const mailFoldersSlice = createSlice({
  name: 'mailFolders',
  initialState,
  reducers: {
    // -----------------------------------------------------------------------
    // Unread count updates (from delta sync)
    // -----------------------------------------------------------------------
    updateFolderCounts(state, action) {
      const { folderId, unreadItemCount, totalItemCount } = action.payload;
      if (state.byId[folderId]) {
        if (unreadItemCount !== undefined) {
          state.byId[folderId].unreadItemCount = unreadItemCount;
        }
        if (totalItemCount !== undefined) {
          state.byId[folderId].totalItemCount = totalItemCount;
        }
      }
    },

    // Increment unread count (when new mail arrives in folder)
    incrementUnread(state, action) {
      const { folderId, by = 1 } = action.payload;
      if (state.byId[folderId]) {
        state.byId[folderId].unreadItemCount =
          (state.byId[folderId].unreadItemCount || 0) + by;
      }
    },

    // Decrement unread count (when message marked read)
    decrementUnread(state, action) {
      const { folderId, by = 1 } = action.payload;
      if (state.byId[folderId]) {
        state.byId[folderId].unreadItemCount = Math.max(
          0,
          (state.byId[folderId].unreadItemCount || 0) - by
        );
      }
    },

    // -----------------------------------------------------------------------
    // Tree UI state
    // -----------------------------------------------------------------------
    toggleFolderExpanded(state, action) {
      const { folderId } = action.payload;
      const idx = state.expandedIds.indexOf(folderId);
      if (idx === -1) {
        state.expandedIds.push(folderId);
      } else {
        state.expandedIds.splice(idx, 1);
      }
    },

    expandFolder(state, action) {
      const { folderId } = action.payload;
      if (!state.expandedIds.includes(folderId)) {
        state.expandedIds.push(folderId);
      }
    },

    collapseFolder(state, action) {
      const { folderId } = action.payload;
      state.expandedIds = state.expandedIds.filter((id) => id !== folderId);
    },

    // -----------------------------------------------------------------------
    // Bulk set entire folder tree (used by graphMailThunks after fetch)
    // -----------------------------------------------------------------------
    setFolderTree(state, action) {
      const { folders, rootIds, childrenByParent } = action.payload;
      state.byId = {};
      folders.forEach((f) => { state.byId[f.id] = f; });

      state.rootIds = composeSortedRootIds(state.byId, rootIds || []);
      state.childrenByParent = childrenByParent || {};

      // Auto-expand inbox
      const inbox = folders.find((f) => f.wellKnownName === 'inbox');
      if (inbox && !state.expandedIds.includes(inbox.id)) {
        state.expandedIds.push(inbox.id);
      }
      state.status = 'ready';
    },

    // -----------------------------------------------------------------------
    // Delta token management
    // -----------------------------------------------------------------------
    setDeltaToken(state, action) {
      state.deltaToken = action.payload;
    },

    // -----------------------------------------------------------------------
    // Upsert a folder (from delta sync or folder creation)
    // -----------------------------------------------------------------------
    upsertFolder(state, action) {
      const folder = action.payload;
      state.byId[folder.id] = { ...(state.byId[folder.id] || {}), ...folder };

      if (!folder.parentFolderId) {
        if (!state.rootIds.includes(folder.id)) {
          state.rootIds.push(folder.id);
        }
      } else {
        if (!state.childrenByParent[folder.parentFolderId]) {
          state.childrenByParent[folder.parentFolderId] = [];
        }
        if (!state.childrenByParent[folder.parentFolderId].includes(folder.id)) {
          state.childrenByParent[folder.parentFolderId].push(folder.id);
        }
      }
    },

    // -----------------------------------------------------------------------
    // Drag-and-drop reordering (custom folders only)
    // -----------------------------------------------------------------------

    /**
     * Move a root-level custom folder before or after a target folder.
     * System folders are never touched.
     */
    reorderRootFolders(state, action) {
      const { dragId, targetId, before } = action.payload;

      // Any folder with a wellKnownName is a system folder — keep them locked
      const systemIds = state.rootIds.filter(id => !!state.byId[id]?.wellKnownName);
      const systemSet = new Set(systemIds);

      let custom = state.rootIds.filter(id => state.byId[id] && !systemSet.has(id));
      const dragIdx = custom.indexOf(dragId);
      if (dragIdx === -1) return; // guard: only custom folders are draggable

      custom.splice(dragIdx, 1);
      const targetIdx = custom.indexOf(targetId);
      const insertAt = targetIdx === -1
        ? custom.length
        : (before ? targetIdx : targetIdx + 1);
      custom.splice(insertAt, 0, dragId);

      state.rootIds = [...systemIds, ...custom];
      saveFolderOrder({ rootCustomOrder: custom });
    },

    /**
     * Move a child folder before or after a target within the same parent.
     */
    reorderChildFolders(state, action) {
      const { parentId, dragId, targetId, before } = action.payload;

      const children = [...(state.childrenByParent[parentId] || [])];
      const dragIdx = children.indexOf(dragId);
      if (dragIdx === -1) return;

      children.splice(dragIdx, 1);
      const targetIdx = children.indexOf(targetId);
      const insertAt = targetIdx === -1
        ? children.length
        : (before ? targetIdx : targetIdx + 1);
      children.splice(insertAt, 0, dragId);

      state.childrenByParent[parentId] = children;
      const { childrenOrder = {} } = loadFolderOrder();
      saveFolderOrder({ childrenOrder: { ...childrenOrder, [parentId]: children } });
    },

    // Remove a folder (deleted by user)
    removeFolder(state, action) {
      const { folderId } = action.payload;
      const folder = state.byId[folderId];
      if (!folder) return;

      delete state.byId[folderId];
      state.rootIds = state.rootIds.filter((id) => id !== folderId);

      if (folder.parentFolderId && state.childrenByParent[folder.parentFolderId]) {
        state.childrenByParent[folder.parentFolderId] =
          state.childrenByParent[folder.parentFolderId].filter((id) => id !== folderId);
      }
      delete state.childrenByParent[folderId];
      state.expandedIds = state.expandedIds.filter((id) => id !== folderId);
    },
  },

  extraReducers: (builder) => {
    // ------------------------------------------------------------------
    // fetchFolders
    // ------------------------------------------------------------------
    builder
      .addCase(fetchFolders.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchFolders.fulfilled, (state, action) => {
        const { folders, childrenByParent, rootIds } = action.payload;

        state.byId = {};
        folders.forEach((folder) => { state.byId[folder.id] = folder; });

        state.rootIds = composeSortedRootIds(state.byId, rootIds || []);
        state.childrenByParent = childrenByParent || {};

        // Auto-expand inbox
        const inbox = folders.find((f) => f.wellKnownName === 'inbox');
        if (inbox && !state.expandedIds.includes(inbox.id)) {
          state.expandedIds.push(inbox.id);
        }
        state.status = 'ready';
      })
      .addCase(fetchFolders.rejected, (state, action) => {
        state.status = 'error';
        state.error = action.payload || 'Failed to load folders';
      });

    // ------------------------------------------------------------------
    // fetchChildFolders
    // ------------------------------------------------------------------
    builder
      .addCase(fetchChildFolders.pending, (state, action) => {
        const { parentId } = action.meta.arg;
        if (!state.loadingIds.includes(parentId)) {
          state.loadingIds.push(parentId);
        }
      })
      .addCase(fetchChildFolders.fulfilled, (state, action) => {
        const { parentId, folders } = action.payload;
        folders.forEach((folder) => {
          state.byId[folder.id] = folder;
        });
        state.childrenByParent[parentId] = folders.map((f) => f.id);
        state.loadingIds = state.loadingIds.filter((id) => id !== parentId);
      })
      .addCase(fetchChildFolders.rejected, (state, action) => {
        const { parentId } = action.meta.arg;
        state.loadingIds = state.loadingIds.filter((id) => id !== parentId);
      });
  },
});

export const {
  setFolderTree,
  updateFolderCounts,
  incrementUnread,
  decrementUnread,
  toggleFolderExpanded,
  expandFolder,
  collapseFolder,
  setDeltaToken,
  upsertFolder,
  removeFolder,
  reorderRootFolders,
  reorderChildFolders,
} = mailFoldersSlice.actions;

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

export const selectFolderById = (state, folderId) =>
  state.mailFolders.byId[folderId];

export const selectRootFolderIds = (state) =>
  state.mailFolders.rootIds;

export const selectChildFolderIds = (state, parentId) =>
  state.mailFolders.childrenByParent[parentId] || [];

export const selectFolderByWellKnownName = (state, wellKnownName) =>
  Object.values(state.mailFolders.byId).find((f) => f.wellKnownName === wellKnownName) || null;

export const selectInboxFolderId = (state) =>
  selectFolderByWellKnownName(state, 'inbox')?.id || null;

export const selectIsFolderExpanded = (state, folderId) =>
  state.mailFolders.expandedIds.includes(folderId);

export const selectFolderLoadStatus = (state) =>
  state.mailFolders.status;

export const selectTotalUnreadCount = (state) =>
  Object.values(state.mailFolders.byId).reduce(
    (sum, folder) => sum + (folder.unreadItemCount || 0),
    0
  );

export default mailFoldersSlice.reducer;
